import { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

// Commission rules - Canvassers
const TEAM_A_CANVASSERS = ['kaleb', 'caleb'];        // Alfredo's team
const TEAM_A_MANAGER = 'alfredo';
const TEAM_B_MANAGER = 'jp';
const JP_TEAM = ['austin', 'tommy', 'andres', 'arnold', 'julio', 'ricky']; // JP's override team

// Commission rules - Telemarketers
const TEAM_C_MANAGER = 'carlos';

// Override rates
const ALFREDO_OVERRIDE = 0.01;
const JP_OVERRIDE = 0.01;
const CARLOS_OVERRIDE = 0.02;

// Parse a PO into base + numeric suffix; rank jobs within each client group (suffix asc).
function parsePO(po) {
  const s = (po || '').trim();
  const m = s.match(/^(.*)-(\d+)$/);
  return m ? { base: m[1].toLowerCase(), suffix: parseInt(m[2], 10) } : { base: s.toLowerCase(), suffix: 1 };
}
function rankWithinClients(jobs) {
  const groups = {};
  jobs.forEach(j => { const { base, suffix } = parsePO(j.po_number); (groups[base] = groups[base] || []).push({ id: j.id, suffix }); });
  const m = {};
  Object.values(groups).forEach(arr => { arr.sort((a, b) => a.suffix - b.suffix); arr.forEach((g, i) => { m[g.id] = i + 1; }); });
  return m;
}

function getCanvasserRate(name) {
  const n = (name || '').toLowerCase().trim();
  if (n === TEAM_A_MANAGER || n === TEAM_B_MANAGER) return 0.03;
  if (TEAM_A_CANVASSERS.includes(n)) return 0.015;
  return 0.015;
}

function getTelemarketerRate(name) {
  const n = (name || '').toLowerCase().trim();
  if (n === TEAM_C_MANAGER) return 0.03;
  return 0.0175;
}

function isTeamA(name) {
  const n = (name || '').toLowerCase().trim();
  return TEAM_A_CANVASSERS.includes(n) || n === TEAM_A_MANAGER;
}

function jobValue(job) {
  // Use effective_value (after split) if available, otherwise full value
  if (job.effective_value !== undefined) return parseFloat(job.effective_value) || 0;
  return (parseFloat(job.contract_amt) || 0) + (parseFloat(job.change_order_amt) || 0);
}

function fullJobValue(job) {
  return (parseFloat(job.contract_amt) || 0) + (parseFloat(job.change_order_amt) || 0);
}

function fmt(n) {
  return '$' + (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}


function getManager(name, type) {
  const n = (name || '').toLowerCase().trim();
  if (type === 'canvasser') {
    if (['kaleb','caleb'].includes(n)) return 'Alfredo';
    if (JP_TEAM.includes(n)) return 'JP';
    return '—';
  } else {
    if (n !== 'carlos') return 'Carlos';
    return '—';
  }
}

function getNextFridays() {
  const fridays = [];
  const d = new Date();
  d.setMonth(d.getMonth() - 6);
  while (d.getDay() !== 5) d.setDate(d.getDate() + 1);
  const end = new Date();
  end.setMonth(end.getMonth() + 3);
  while (d <= end) {
    fridays.push(new Date(d).toISOString().slice(0, 10));
    d.setDate(d.getDate() + 14);
  }
  return fridays.reverse();
}

function buildCanvasserBreakdown(jobs, selected) {
  const sel = jobs.filter(j => selected[j.id]);
  const byName = {};
  sel.forEach(job => {
    const name = job.staff_name?.trim();
    if (!name) return;
    if (!byName[name]) byName[name] = [];
    byName[name].push(job);
  });
  const canvassers = Object.entries(byName).map(([name, jobs]) => {
    const total = jobs.reduce((s, j) => s + jobValue(j), 0);
    const rate = getCanvasserRate(name);
    return { name, jobs, total, rate, commission: total * rate };
  });
  const teamA = canvassers.filter(c => isTeamA(c.name) && c.name.toLowerCase() !== TEAM_A_MANAGER);
  const teamB = canvassers.filter(c => JP_TEAM.includes(c.name.toLowerCase()));
  const alfredo = canvassers.find(c => c.name.toLowerCase() === TEAM_A_MANAGER);
  const jp = canvassers.find(c => c.name.toLowerCase() === TEAM_B_MANAGER);
  const teamATotal = teamA.reduce((s, c) => s + c.total, 0);
  const teamBTotal = teamB.reduce((s, c) => s + c.total, 0);

  // Overrides pay on the FIRST job per client (rank 1) only, on the FULL job value.
  const rank = rankWithinClients(sel);
  const overrideBase = teamList => teamList.reduce((sum, c) =>
    sum + c.jobs.filter(j => (rank[j.id] || 1) === 1).reduce((s, j) => s + fullJobValue(j), 0), 0);
  const alfredoOverride = overrideBase(teamA) * ALFREDO_OVERRIDE;
  const jpOverride = overrideBase(teamB) * JP_OVERRIDE;
  return { canvassers, teamA, teamB, alfredo, jp, teamATotal, teamBTotal, alfredoOverride, jpOverride };
}

function buildTelemarketerBreakdown(jobs, selected) {
  const sel = jobs.filter(j => selected[j.id]);
  const byName = {};
  sel.forEach(job => {
    const name = job.staff_name?.trim();
    if (!name) return;
    if (!byName[name]) byName[name] = [];
    byName[name].push(job);
  });
  const telemarketers = Object.entries(byName).map(([name, jobs]) => {
    const total = jobs.reduce((s, j) => s + jobValue(j), 0);
    const rate = getTelemarketerRate(name);
    return { name, jobs, total, rate, commission: total * rate };
  });
  const teamC = telemarketers.filter(t => t.name.toLowerCase() !== TEAM_C_MANAGER);
  const carlos = telemarketers.find(t => t.name.toLowerCase() === TEAM_C_MANAGER);
  const teamCTotal = teamC.reduce((s, t) => s + t.total, 0);
  const rankTel = rankWithinClients(sel);
  const carlosOverrideBase = teamC.reduce((sum, t) =>
    sum + t.jobs.filter(j => (rankTel[j.id] || 1) === 1).reduce((s, j) => s + fullJobValue(j), 0), 0);
  const carlosOverride = carlosOverrideBase * CARLOS_OVERRIDE;
  return { telemarketers, teamC, carlos, teamCTotal, carlosOverride };
}

export default function StaffPayroll() {
  const [tab, setTab] = useState('canvassers');
  const [subTab, setSubTab] = useState('new');
  const [canvasserJobs, setCanvasserJobs] = useState([]);
  const [telemarketerJobs, setTelemarketerJobs] = useState([]);
  const [selectedCanvassers, setSelectedCanvassers] = useState({});
  const [selectedTelemarketers, setSelectedTelemarketers] = useState({});
  const [payDate, setPayDate] = useState(getNextFridays()[1] || '');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [undoing, setUndoing] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [periodEntries, setPeriodEntries] = useState([]);

  const fridays = getNextFridays();

  useEffect(() => { loadAll(); }, [payDate]);

  async function loadAll() {
    setLoading(true);
    try {
      const [c, t, p] = await Promise.all([
        api.get(`/canvasser-commission/unpaid/canvassers?pay_date=${payDate}`),
        api.get(`/canvasser-commission/unpaid/telemarketers?pay_date=${payDate}`),
        api.get('/canvasser-commission/periods'),
      ]);
      setCanvasserJobs(Array.isArray(c.data) ? c.data : []);
      setTelemarketerJobs(Array.isArray(t.data) ? t.data : []);
      setPeriods(Array.isArray(p.data) ? p.data : []);
    } catch {} finally { setLoading(false); }
  }

  async function undoPayroll(date, type) {
    if (!window.confirm(`Undo payroll for ${date} (${type}s)? This will return all jobs to unpaid.`)) return;
    setUndoing(date + type);
    try {
      await api.delete(`/canvasser-commission/period/${date}/${type}`);
      toast.success('Payroll reversed — jobs returned to unpaid');
      loadAll();
      setPeriods(prev => prev.filter(p => !(p.pay_date === date && p.staff_type === type)));
      setSelectedPeriod(null);
    } catch { toast.error('Failed to undo'); }
    finally { setUndoing(null); }
  }

  async function loadPeriodEntries(date) {
    try {
      const res = await api.get(`/canvasser-commission/period/${date}`);
      setPeriodEntries(Array.isArray(res.data) ? res.data : []);
    } catch {}
  }

  async function processPayroll(type) {
    if (!payDate) return toast.error('Select a pay date');
    const jobs = type === 'canvasser' ? canvasserJobs : telemarketerJobs;
    const selected = type === 'canvasser' ? selectedCanvassers : selectedTelemarketers;
    const sel = jobs.filter(j => selected[j.id]);
    if (sel.length === 0) return toast.error('Select at least one job');

    const entries = [];

    if (type === 'canvasser') {
      const bd = buildCanvasserBreakdown(jobs, selected);
      const rank = rankWithinClients(jobs.filter(j => selected[j.id]));
      bd.canvassers.forEach(c => {
        c.jobs.forEach(job => {
          entries.push({ job_id: job.id, staff_name: c.name, staff_type: 'canvasser', job_value: jobValue(job), commission_rate: c.rate, commission_amt: jobValue(job) * c.rate });
        });
      });
      // Alfredo override on his team — rank-1 jobs only, 1% of full job value
      bd.teamA.forEach(c => {
        c.jobs.filter(job => (rank[job.id] || 1) === 1).forEach(job => {
          entries.push({ job_id: job.id, staff_name: 'Alfredo', staff_type: 'canvasser', job_value: fullJobValue(job), commission_rate: ALFREDO_OVERRIDE, commission_amt: fullJobValue(job) * ALFREDO_OVERRIDE, override_for: c.name });
        });
      });
      // JP override on his team — rank-1 jobs only, 1% of full job value
      bd.teamB.forEach(c => {
        c.jobs.filter(job => (rank[job.id] || 1) === 1).forEach(job => {
          entries.push({ job_id: job.id, staff_name: 'JP', staff_type: 'canvasser', job_value: fullJobValue(job), commission_rate: JP_OVERRIDE, commission_amt: fullJobValue(job) * JP_OVERRIDE, override_for: c.name });
        });
      });
    } else {
      const bd = buildTelemarketerBreakdown(jobs, selected);
      const rank = rankWithinClients(jobs.filter(j => selected[j.id]));
      bd.telemarketers.forEach(t => {
        t.jobs.forEach(job => {
          entries.push({ job_id: job.id, staff_name: t.name, staff_type: 'telemarketer', job_value: jobValue(job), commission_rate: t.rate, commission_amt: jobValue(job) * t.rate });
        });
      });
      // Carlos override on Team C — rank-1 jobs only, 2% of full job value
      bd.teamC.forEach(t => {
        t.jobs.filter(job => (rank[job.id] || 1) === 1).forEach(job => {
          entries.push({ job_id: job.id, staff_name: 'Carlos', staff_type: 'telemarketer', job_value: fullJobValue(job), commission_rate: CARLOS_OVERRIDE, commission_amt: fullJobValue(job) * CARLOS_OVERRIDE, override_for: t.name });
        });
      });
    }

    setProcessing(true);
    try {
      await api.post('/canvasser-commission/process', { pay_date: payDate, entries });
      toast.success('Payroll processed!');
      if (type === 'canvasser') setSelectedCanvassers({});
      else setSelectedTelemarketers({});
      loadAll();
      setSubTab('history');
    } catch { toast.error('Failed to process payroll'); }
    finally { setProcessing(false); }
  }

  const canvasserBD = buildCanvasserBreakdown(canvasserJobs, selectedCanvassers);
  const telemarketerBD = buildTelemarketerBreakdown(telemarketerJobs, selectedTelemarketers);
  const canvasserTotal = canvasserBD.canvassers.reduce((s, c) => s + c.commission, 0) + canvasserBD.alfredoOverride + canvasserBD.jpOverride;
  const telemarketerTotal = telemarketerBD.telemarketers.reduce((s, t) => s + t.commission, 0) + telemarketerBD.carlosOverride;

  const canvasserPeriods = periods.filter(p => p.staff_type === 'canvasser');
  const telemarketerPeriods = periods.filter(p => p.staff_type === 'telemarketer');

  return (
    <>
      <div className="page-header">
        <h2>Staff Payroll</h2>
        <div className="page-header-actions">
          <button className={`btn btn-sm ${subTab === 'new' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setSubTab('new')}>New Payroll</button>
          <button className={`btn btn-sm ${subTab === 'history' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setSubTab('history')}>History</button>
        </div>
      </div>

      <div className="page-body">
        {/* Team tabs */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '2px solid #eee' }}>
          {[['canvassers', '🏃 Canvassers'], ['telemarketers', '📞 Telemarketers']].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
              style={{ padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer',
                fontWeight: tab === key ? 700 : 400, color: tab === key ? '#0D3B2E' : '#888',
                borderBottom: tab === key ? '2px solid #0D3B2E' : '2px solid transparent',
                marginBottom: -2, fontSize: 14 }}>
              {label}
            </button>
          ))}
        </div>

        {subTab === 'new' && (
          <PayrollNew
            jobs={tab === 'canvassers' ? canvasserJobs : telemarketerJobs}
            selected={tab === 'canvassers' ? selectedCanvassers : selectedTelemarketers}
            setSelected={tab === 'canvassers' ? setSelectedCanvassers : setSelectedTelemarketers}
            payDate={payDate} setPayDate={setPayDate} fridays={fridays}
            breakdown={tab === 'canvassers' ? canvasserBD : telemarketerBD}
            totalCommission={tab === 'canvassers' ? canvasserTotal : telemarketerTotal}
            type={tab === 'canvassers' ? 'canvasser' : 'telemarketer'}
            onProcess={() => processPayroll(tab === 'canvassers' ? 'canvasser' : 'telemarketer')}
            processing={processing} loading={loading}
          />
        )}

        {subTab === 'history' && (
          <PayrollHistory
            periods={tab === 'canvassers' ? canvasserPeriods : telemarketerPeriods}
            selectedPeriod={selectedPeriod}
            setSelectedPeriod={(date) => { setSelectedPeriod(date); loadPeriodEntries(date); }}
            periodEntries={periodEntries.filter(e => e.staff_type === (tab === 'canvassers' ? 'canvasser' : 'telemarketer'))}
          />
        )}
      </div>
    </>
  );
}

function PayrollNew({ jobs, selected, setSelected, payDate, setPayDate, fridays, breakdown, totalCommission, type, onProcess, processing, loading }) {
  const selectedCount = Object.values(selected).filter(Boolean).length;
  const isCanvasser = type === 'canvasser';

  function toggle(id) { setSelected(prev => ({ ...prev, [id]: !prev[id] })); }
  function selectAll() { const a = {}; jobs.forEach(j => { a[j.id] = true; }); setSelected(a); }
  function selectNone() { setSelected({}); }

  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div style={{ flex: 2, minWidth: 400 }}>
        <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', padding: 18, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#0D3B2E' }}>Select Pay Date</div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>Bi-weekly Fridays</div>
            </div>
            <select className="form-select" value={payDate} onChange={e => setPayDate(e.target.value)} style={{ width: 180 }}>
              {fridays.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
        </div>

        <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <div style={{ padding: '12px 18px', background: '#0D3B2E', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontWeight: 700 }}>Unpaid Jobs ({jobs.length})</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={selectAll} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>All</button>
              <button onClick={selectNone} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>None</button>
            </div>
          </div>
          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>Loading…</div>
          ) : jobs.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No unpaid jobs</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #eee', background: '#f9f9f9' }}>
                  <th style={{ padding: '8px 14px', width: 32 }}></th>
                  <th style={{ padding: '8px 14px', textAlign: 'left' }}>Customer</th>
                  <th style={{ padding: '8px 14px', textAlign: 'left' }}>{isCanvasser ? 'Canvasser' : 'Telemarketer'}</th>
                  <th style={{ padding: '8px 14px', textAlign: 'right' }}>Value</th>
                  <th style={{ padding: '8px 14px', textAlign: 'right' }}>Commission</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map(job => {
                  const val = jobValue(job);
                  const rate = isCanvasser ? getCanvasserRate(job.staff_name) : getTelemarketerRate(job.staff_name);
                  return (
                    <tr key={job.id} onClick={() => toggle(job.id)}
                      style={{ borderBottom: '1px solid #f5f5f5', cursor: 'pointer', background: selected[job.id] ? '#e8f5e9' : '#fff' }}>
                      <td style={{ padding: '10px 14px' }}>
                        <input type="checkbox" checked={!!selected[job.id]} onChange={() => toggle(job.id)}
                          style={{ width: 16, height: 16, accentColor: '#1B5E20' }} onClick={e => e.stopPropagation()} />
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        <div style={{ fontWeight: 600 }}>{job.customer_name}</div>
                        <div style={{ fontSize: 11, color: '#888' }}>{job.po_number}</div>
                      </td>
                      <td style={{ padding: '10px 14px', color: '#555' }}>{job.staff_name}</td>
                        <td style={{ padding: '10px 14px', color: '#888', fontSize: 12 }}>{getManager(job.staff_name, type)}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 600 }}>${val.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', color: '#1B5E20', fontWeight: 600 }}>${(val * rate).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Breakdown panel */}
      <div style={{ flex: 1, minWidth: 280, position: 'sticky', top: 80 }}>
        <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', padding: 18, marginBottom: 14 }}>
          <div style={{ fontWeight: 700, color: '#0D3B2E', marginBottom: 14, fontSize: 14 }}>
            Commission Breakdown — {selectedCount} jobs
          </div>

          {selectedCount === 0 ? (
            <div style={{ color: '#aaa', fontSize: 13 }}>Select jobs to see breakdown</div>
          ) : isCanvasser ? (
            <>
              {(breakdown.teamA.length > 0 || breakdown.alfredo) && (
                <TeamBreakdown title="Team A" color="#1B5E20">
                  {breakdown.teamA.map(c => <CommRow key={c.name} name={c.name} jobs={c.jobs.length} rate={c.rate} commission={c.commission} />)}
                  {breakdown.alfredo && <CommRow name="Alfredo (own 3%)" jobs={breakdown.alfredo.jobs.length} rate={0.03} commission={breakdown.alfredo.commission} />}
                  {breakdown.alfredoOverride > 0 && <CommRow name="Alfredo (1% override on Team A)" jobs={breakdown.teamA.reduce((s,c)=>s+c.jobs.length,0)} rate={0.01} commission={breakdown.alfredoOverride} isOverride />}
                </TeamBreakdown>
              )}
              {(breakdown.teamB.length > 0 || breakdown.jp) && (
                <TeamBreakdown title="Team B" color="#1565C0">
                  {breakdown.teamB.map(c => <CommRow key={c.name} name={c.name} jobs={c.jobs.length} rate={c.rate} commission={c.commission} />)}
                  {breakdown.jp && <CommRow name="JP (own 3%)" jobs={breakdown.jp.jobs.length} rate={0.03} commission={breakdown.jp.commission} />}
                  {breakdown.jpOverride > 0 && <CommRow name="JP (1.5% override on Team B)" jobs={breakdown.teamB.reduce((s,c)=>s+c.jobs.length,0)} rate={0.015} commission={breakdown.jpOverride} isOverride />}
                </TeamBreakdown>
              )}
            </>
          ) : (
            <TeamBreakdown title="Team C — Telemarketers" color="#6a1a9a">
              {breakdown.teamC.map(t => <CommRow key={t.name} name={t.name} jobs={t.jobs.length} rate={t.rate} commission={t.commission} />)}
              {breakdown.carlos && <CommRow name="Carlos (own 3%)" jobs={breakdown.carlos.jobs.length} rate={0.03} commission={breakdown.carlos.commission} />}
              {breakdown.carlosOverride > 0 && <CommRow name="Carlos (2% override on Team C)" jobs={breakdown.teamC.reduce((s,t)=>s+t.jobs.length,0)} rate={0.02} commission={breakdown.carlosOverride} isOverride />}
            </TeamBreakdown>
          )}

          <div style={{ borderTop: '2px solid #0D3B2E', paddingTop: 12, display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 15, marginTop: 8 }}>
            <span>Total Payout</span>
            <span style={{ color: '#1B5E20' }}>{fmt(totalCommission)}</span>
          </div>
        </div>

        <button className="btn btn-primary" style={{ width: '100%', fontSize: 15, padding: '12px' }}
          onClick={onProcess} disabled={processing || selectedCount === 0}>
          {processing ? 'Processing…' : `Process Payroll — ${payDate}`}
        </button>
      </div>
    </div>
  );
}

function PayrollHistory({ periods, selectedPeriod, setSelectedPeriod, periodEntries }) {
  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div style={{ width: 260, flexShrink: 0 }}>
        <div style={{ fontWeight: 700, color: '#0D3B2E', marginBottom: 12 }}>Pay Periods</div>
        {periods.length === 0 ? (
          <div style={{ color: '#aaa', fontSize: 13 }}>No pay periods yet</div>
        ) : periods.map(p => (
          <div key={p.pay_date} onClick={() => setSelectedPeriod(p.pay_date)}
            style={{ background: '#fff', borderRadius: 8, padding: '12px 14px', marginBottom: 8, cursor: 'pointer',
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: selectedPeriod === p.pay_date ? '2px solid #0D3B2E' : '2px solid transparent' }}>
            <div style={{ fontWeight: 700, color: '#0D3B2E' }}>{p.pay_date}</div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{p.job_count} jobs</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1B5E20', marginTop: 4 }}>{fmt(p.total_commission)}</div>
          </div>
        ))}
      </div>

      {selectedPeriod && (
        <div style={{ flex: 1, minWidth: 400, background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', background: '#0D3B2E', color: '#fff', fontWeight: 700, fontSize: 15 }}>
            Pay Period — {selectedPeriod}
          </div>
          <div style={{ padding: 18 }}>
            {Object.entries(
              periodEntries.reduce((acc, e) => {
                const key = e.staff_name + (e.override_for ? ` (override on ${e.override_for})` : '');
                if (!acc[key]) acc[key] = { entries: [], total: 0 };
                acc[key].entries.push(e);
                acc[key].total += e.commission_amt;
                return acc;
              }, {})
            ).map(([name, data]) => (
              <div key={name} style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #f0f0f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={{ fontWeight: 700, color: '#0D3B2E' }}>{name}</div>
                  <div style={{ fontWeight: 700, color: '#1B5E20' }}>{fmt(data.total)}</div>
                </div>
                {data.entries.map(e => (
                  <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#555', padding: '3px 0' }}>
                    <span>{e.customer_name} ({e.po_number})</span>
                    <span>{fmt(e.commission_amt)}</span>
                  </div>
                ))}
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 15, borderTop: '2px solid #0D3B2E', paddingTop: 12 }}>
              <span>Total Payout</span>
              <span style={{ color: '#1B5E20' }}>{fmt(periodEntries.reduce((s, e) => s + e.commission_amt, 0))}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TeamBreakdown({ title, color, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: 'uppercase', marginBottom: 8, letterSpacing: 1 }}>{title}</div>
      {children}
    </div>
  );
}

function CommRow({ name, jobs, rate, commission, isOverride }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 13, borderBottom: '1px solid #f5f5f5' }}>
      <span style={{ color: isOverride ? '#888' : '#333', fontStyle: isOverride ? 'italic' : 'normal' }}>
        {name} <span style={{ color: '#aaa', fontSize: 11 }}>({jobs} jobs · {(rate*100).toFixed(2)}%)</span>
      </span>
      <span style={{ fontWeight: 600, color: '#1B5E20' }}>{fmt(commission)}</span>
    </div>
  );
}
